import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayerHeadshot from "../PlayerHeadshot";
import TeamLogo from "../TeamLogo";
import { homeCardHover } from "../home/homeStyles";
import { colors } from "../../theme";
import { BRAND_COLORS, brandRgba } from "../../utils/brandAssets";
import { featureChipAriaLabel } from "./similarSeasonsFeatureGlossary";

const RankBadge = ({ rank }) => {
  const isClosest = rank === 1;
  return (
    <Box
      aria-hidden
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.75rem",
        fontWeight: 800,
        fontVariantNumeric: "tabular-nums",
        bgcolor: isClosest ? brandRgba(BRAND_COLORS.cyan, 0.18) : "action.hover",
        color: isClosest ? BRAND_COLORS.cyan : "text.secondary",
        border: `1px solid ${
          isClosest ? brandRgba(BRAND_COLORS.cyan, 0.4) : colors.border
        }`,
      }}
    >
      {rank}
    </Box>
  );
};

const FeatureChip = ({ label, gloss }) => {
  const chip = (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      tabIndex={0}
      aria-label={featureChipAriaLabel(label, gloss)}
      sx={{ height: 24, fontWeight: 700, borderColor: colors.border }}
    />
  );
  if (!gloss) return chip;
  return (
    <Tooltip title={gloss} enterDelay={200} arrow>
      {chip}
    </Tooltip>
  );
};

const SimilarSeasonCard = ({ item, onSelect }) => {
  const [open, setOpen] = useState(false);
  const href = item.href;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 1.5,
        border: `1px solid ${colors.border}`,
        overflow: "hidden",
        ...homeCardHover,
        cursor: href ? "pointer" : "default",
      }}
    >
      <Box
        component={href ? RouterLink : "div"}
        to={href || undefined}
        onClick={() => href && onSelect?.(item)}
        aria-label={`${item.playerName}, ${item.season}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          gap: 1.5,
          p: 1.75,
          pb: item.comparisons?.length ? 0.5 : 1.75,
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <PlayerHeadshot
            playerId={item.playerId}
            sport={item.sport}
            size={56}
            showTooltip={false}
            showLoading={false}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <RankBadge rank={item.rank} />
              <Tooltip title={item.tooltip} placement="top" enterDelay={400}>
                <Typography
                  variant="caption"
                  color={item.rank === 1 ? BRAND_COLORS.cyan : "text.secondary"}
                  fontWeight={700}
                  noWrap
                >
                  {item.rankLabel}
                </Typography>
              </Tooltip>
            </Stack>
            <Typography variant="body1" fontWeight={700} noWrap>
              {item.playerName}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
              {item.teamLogoId ? (
                <TeamLogo
                  sport={item.sport}
                  franchiseId={item.teamLogoId}
                  teamName={item.teamLogoId}
                  size={16}
                />
              ) : null}
              <Typography variant="caption" color="text.secondary" noWrap>
                {item.season}
                {item.teamLabel ? ` · ${item.teamLabel}` : ""}
              </Typography>
            </Stack>
          </Box>
        </Box>

        {(item.strongestFeatures || item.strongestLabels)?.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 0.75 }}
            >
              Similar because of
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {(
                item.strongestFeatures ||
                item.strongestLabels.map((label) => ({ label }))
              ).map((feature) => (
                <FeatureChip
                  key={feature.label}
                  label={feature.label}
                  gloss={feature.gloss}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      {item.comparisons?.length > 0 && (
        <Box sx={{ px: 1.75, pb: 1.25 }}>
          <Button
            size="small"
            onClick={() => setOpen((value) => !value)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: open ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
            }
            sx={{
              ml: -0.75,
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Compare numbers
          </Button>
          <Collapse in={open} unmountOnExit>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 0.75 }}
            >
              This season vs theirs
            </Typography>
            <Stack spacing={0.5}>
              {item.comparisons.map((row) => (
                <Stack
                  key={row.label}
                  direction="row"
                  justifyContent="space-between"
                  gap={1}
                >
                  <Typography variant="caption" color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {row.source} vs {row.match}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default SimilarSeasonCard;
